(function () {
  const ROOT_SELECTOR = '[data-build-box-root]';

  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    cents = parseInt(cents, 10) || 0;
    const formatString = format || '{{amount}}';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const match = formatString.match(placeholderRegex);
    if (!match) return String(cents / 100);

    function formatWithDelimiters(number, precision, thousands, decimal) {
      thousands = thousands || ',';
      decimal = decimal || '.';
      number = (number / 100.0).toFixed(precision);
      const parts = number.split('.');
      const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const centsAmount = parts[1] ? decimal + parts[1] : '';
      return dollarsAmount + centsAmount;
    }

    let value = '';
    switch (match[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }
    return formatString.replace(placeholderRegex, value);
  }

  function interpolate(template, vars) {
    if (!template) return '';
    return template.replace(/\[(\w+)\]|\{\{\s*(\w+)\s*\}\}/g, (_, bracketKey, braceKey) => {
      const key = bracketKey || braceKey;
      return vars[key] != null ? vars[key] : '';
    });
  }

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  class BuildBox {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId;
      const configEl = document.getElementById(`BuildBoxConfig-${this.sectionId}`);
      if (!configEl) return;

      this.config = JSON.parse(configEl.textContent);
      this.strings = this.config.strings || {};
      this.syncHideVariantPricesFlag();
      this.sizes = this.config.sizes || [];
      this.sizeByBlockId = new Map(this.sizes.map((s) => [s.blockId, s]));
      this.urlParamKey = this.config.urlParamKey || 'box';
      this.sizeByUrlLabel = new Map();
      this.sizes.forEach((size) => {
        const slug = this.normalizeUrlLabel(size.urlLabel);
        if (slug) this.sizeByUrlLabel.set(slug, size.blockId);
      });

      this.activeSize = null;
      this.rows = [];
      this.rowOrder = [];
      this.modalRowId = null;
      /** @type {number[]} variant ids selected in the open modal (order preserved) */
      this.modalPendingVariantIds = [];
      this.draggedRowId = null;
      /** @type {Map<string, (string|null)[][]>} blockId → variant titles by row index and slot */
      this.sizeSelectionCache = new Map();

      this.cacheDom();
      this.bindEvents();
      this.initFromUrl();
    }

    normalizeUrlLabel(value) {
      return String(value || '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/g, '');
    }

    syncHideVariantPricesFlag() {
      let hide = false;
      const configEl = document.getElementById(`BuildBoxConfig-${this.sectionId}`);
      if (configEl) {
        try {
          hide = JSON.parse(configEl.textContent).hideVariantPrices === true;
        } catch (err) {
          hide = false;
        }
      }
      if (!hide && this.root?.dataset?.hideVariantPrices === 'true') {
        hide = true;
      }
      this.hideVariantPrices = hide;
      if (this.root) {
        this.root.classList.toggle('build-box--hide-variant-prices', hide);
        this.root.dataset.hideVariantPrices = hide ? 'true' : 'false';
      }
    }

    shouldHideVariantPrices() {
      this.syncHideVariantPricesFlag();
      return this.hideVariantPrices === true;
    }

    updateUrlForSize(size) {
      if (typeof window.history.replaceState !== 'function') return;
      const url = new URL(window.location.href);
      const slug = size?.urlLabel ? this.normalizeUrlLabel(size.urlLabel) : '';
      if (slug) {
        url.searchParams.set(this.urlParamKey, slug);
      } else {
        url.searchParams.delete(this.urlParamKey);
      }
      window.history.replaceState({}, '', url);
    }

    clearSizeFromUrl() {
      if (typeof window.history.replaceState !== 'function') return;
      const url = new URL(window.location.href);
      if (!url.searchParams.has(this.urlParamKey)) return;
      url.searchParams.delete(this.urlParamKey);
      window.history.replaceState({}, '', url);
    }

    initFromUrl() {
      const url = new URL(window.location.href);
      const raw = url.searchParams.get(this.urlParamKey);
      if (!raw) return;
      const blockId = this.sizeByUrlLabel.get(this.normalizeUrlLabel(raw));
      if (blockId) this.openBuilder(blockId, { skipUrlUpdate: true });
    }

    cacheDom() {
      this.chooseEl = this.root.querySelector('[data-build-box-choose]');
      this.builderEl = this.root.querySelector('[data-build-box-builder]');
      this.rowsEl = this.root.querySelector('[data-build-box-rows]');
      this.upgradeEl = this.root.querySelector('[data-build-box-upgrade]');
      this.upgradeActionsEl = this.root.querySelector('[data-build-box-upgrade-actions]');
      this.modalEl = this.root.querySelector('[data-build-box-modal]');
      this.modalGridEl = this.root.querySelector('[data-build-box-modal-grid]');
      this.modalTitleEl = this.root.querySelector('[data-build-box-modal-title]');
      this.modalSelectionEl = this.root.querySelector('[data-build-box-modal-selection]');
      this.modalSelectHintEl = this.root.querySelector('[data-build-box-modal-select-hint]');
      this.modalSearchEl = this.root.querySelector('[data-build-box-modal-search]');
      this.modalDoneBtn = this.root.querySelector('[data-build-box-modal-done]');
      this.modalDoneDefaultLabel =
        this.modalDoneBtn?.textContent?.trim() || this.strings.modalDoneLabel || 'Done';

      this.progressCountEl = this.root.querySelector('[data-build-box-progress-count]');
      this.progressPercentEl = this.root.querySelector('[data-build-box-progress-percent]');
      this.progressSizeEl = this.root.querySelector('[data-build-box-progress-size]');
      this.progressFillEl = this.root.querySelector('[data-build-box-progress-fill]');
      this.progressTrackEl = this.root.querySelector('.build-box__progress-track');
      this.builderStatusEl = this.root.querySelector('[data-build-box-builder-status]');
      this.builderLeadEl = this.root.querySelector('[data-build-box-builder-lead]');

      this.summaryModalEl = this.root.querySelector('[data-build-box-summary-modal]');
      this.summaryBlocks = [...this.root.querySelectorAll('[data-build-box-summary]')].map((root) => ({
        root,
        titleEl: root.querySelector('[data-build-box-summary-title]'),
        emptyEl: root.querySelector('[data-build-box-summary-empty]'),
        listEl: root.querySelector('[data-build-box-summary-list]'),
        sweetsLabelEl: root.querySelector('[data-build-box-summary-sweets-label]'),
        sweetsPriceEl: root.querySelector('[data-build-box-summary-sweets-price]'),
        totalEl: root.querySelector('[data-build-box-summary-total]'),
        noteEl: root.querySelector('[data-build-box-summary-note]'),
        progressFillEl: root.querySelector('[data-build-box-summary-progress-fill]'),
      }));
      this.addToCartBtns = this.root.querySelectorAll('[data-build-box-add-to-cart]');
      this.stickyCountEl = this.root.querySelector('[data-build-box-sticky-count]');
      this.stickyTotalEl = this.root.querySelector('[data-build-box-sticky-total]');
    }

    bindEvents() {
      this.root.addEventListener('click', (e) => {
        if (
          e.target.closest(
            '.build-box__media-slider .css-slider-dot, .build-box__media-slider .css-slider-button'
          )
        ) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }

        const selectLink = e.target.closest('[data-build-box-select]');
        if (selectLink) {
          const blockId = selectLink.dataset.buildBoxBlockId;
          if (blockId && this.sizeByBlockId.has(blockId)) {
            e.preventDefault();
            this.openBuilder(blockId);
          }
          return;
        }

        if (e.target.closest('[data-build-box-change-size]')) {
          e.preventDefault();
          this.showChooseStep();
          return;
        }

        const openRowControl = e.target.closest('[data-build-box-open-row]');
        if (openRowControl) {
          const rowEl = openRowControl.closest('[data-build-box-row]');
          const rowId = rowEl?.dataset.rowId;
          if (rowId) this.openModal(rowId);
          return;
        }

        const removeRowControl = e.target.closest('[data-build-box-remove-row]');
        if (removeRowControl) {
          const rowEl = removeRowControl.closest('[data-build-box-row]');
          if (rowEl) {
            const slotIndexRaw = removeRowControl.dataset.slotIndex;
            if (slotIndexRaw != null && slotIndexRaw !== '') {
              this.setRowSlotVariant(rowEl.dataset.rowId, parseInt(slotIndexRaw, 10), null);
            } else {
              this.clearRowSelections(rowEl.dataset.rowId);
            }
            this.render();
          }
          return;
        }

        if (e.target.closest('[data-build-box-summary-open]')) {
          e.preventDefault();
          this.openSummaryModal();
          return;
        }

        if (e.target.closest('[data-build-box-summary-close]')) {
          e.preventDefault();
          this.closeSummaryModal();
          return;
        }

        if (e.target.closest('[data-build-box-modal-close]')) {
          e.preventDefault();
          this.closeModal();
          return;
        }

        if (e.target.closest('[data-build-box-modal-done]')) {
          e.preventDefault();
          this.commitModalSelection();
          return;
        }

        if (e.target.closest('[data-build-box-add-to-cart]')) {
          e.preventDefault();
          this.addToCartAndCheckout();
          return;
        }

        const variantBtn = e.target.closest('[data-build-box-variant-option]');
        if (variantBtn && this.modalEl && !this.modalEl.hidden) {
          e.preventDefault();
          if (variantBtn.disabled) return;
          this.toggleModalVariant(parseInt(variantBtn.dataset.variantId, 10));
          return;
        }

        const upgradeBtn = e.target.closest('[data-build-box-upgrade-size]');
        if (upgradeBtn) {
          e.preventDefault();
          const blockId = upgradeBtn.dataset.blockId;
          if (blockId) this.upgradeSize(blockId);
        }
      });

      this.modalSearchEl?.addEventListener('input', () => {
        this.renderModalVariants(this.modalSearchEl.value || '');
      });

      document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        if (
          this.summaryModalEl &&
          !this.summaryModalEl.hidden &&
          this.summaryModalEl.classList.contains('is-open')
        ) {
          this.closeSummaryModal();
          return;
        }
        if (this.modalEl && !this.modalEl.hidden) {
          this.closeModal();
        }
      });

      this.rowsEl?.addEventListener('dragstart', (e) => {
        const handle = e.target.closest('[data-build-box-drag-handle]');
        if (!handle) return;
        const row = handle.closest('[data-build-box-row]');
        if (!row) return;
        this.draggedRowId = row.dataset.rowId;
        row.classList.add('is-dragging');
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', this.draggedRowId);
      });

      this.rowsEl?.addEventListener('dragend', (e) => {
        const row = e.target.closest('[data-build-box-row]');
        row?.classList.remove('is-dragging');
        this.draggedRowId = null;
        this.rowsEl.querySelectorAll('[data-build-box-row]').forEach((el) => el.classList.remove('is-drop-target'));
      });

      this.rowsEl?.addEventListener('dragover', (e) => {
        const row = e.target.closest('[data-build-box-row]');
        if (!row || row.dataset.rowId === this.draggedRowId) return;
        e.preventDefault();
        this.rowsEl.querySelectorAll('[data-build-box-row]').forEach((el) => el.classList.remove('is-drop-target'));
        row.classList.add('is-drop-target');
      });

      this.rowsEl?.addEventListener('drop', (e) => {
        const targetRow = e.target.closest('[data-build-box-row]');
        if (!targetRow || !this.draggedRowId) return;
        e.preventDefault();
        const targetId = targetRow.dataset.rowId;
        if (targetId === this.draggedRowId) return;
        const order = [...this.rowOrder];
        const from = order.indexOf(this.draggedRowId);
        const to = order.indexOf(targetId);
        if (from === -1 || to === -1) return;
        order.splice(from, 1);
        order.splice(to, 0, this.draggedRowId);
        this.rowOrder = order;
        this.persistRowOrderToAllSizeCaches();
        this.renderRows();
      });
    }

    getSweetsPerRow(size, rowIndex) {
      const arr = size?.sweetsPerRow;
      if (Array.isArray(arr) && arr[rowIndex] != null) {
        const n = parseInt(arr[rowIndex], 10);
        return Number.isFinite(n) && n > 0 ? n : 1;
      }
      return 1;
    }

    totalSlotCount(size = this.activeSize) {
      if (!size) return 0;
      let total = 0;
      for (let i = 0; i < size.rowCount; i++) {
        total += this.getSweetsPerRow(size, i);
      }
      return total;
    }

    normalizeCacheRowEntry(entry, capacity) {
      const titles = Array(capacity).fill(null);
      if (Array.isArray(entry)) {
        for (let i = 0; i < capacity; i++) {
          titles[i] = entry[i] ?? null;
        }
      } else if (typeof entry === 'string' && entry) {
        titles[0] = entry;
      }
      return titles;
    }

    ensureSelectionCache() {
      for (const size of this.sizes) {
        if (!this.sizeSelectionCache.has(size.blockId)) {
          this.sizeSelectionCache.set(size.blockId, []);
        }
        const cache = this.sizeSelectionCache.get(size.blockId);
        while (cache.length < size.rowCount) {
          const rowIndex = cache.length;
          cache.push(Array(this.getSweetsPerRow(size, rowIndex)).fill(null));
        }
        if (cache.length > size.rowCount) cache.length = size.rowCount;
        for (let i = 0; i < size.rowCount; i++) {
          const capacity = this.getSweetsPerRow(size, i);
          cache[i] = this.normalizeCacheRowEntry(cache[i], capacity);
        }
      }
    }

    normalizeVariantTitle(title) {
      return String(title || '')
        .trim()
        .toLowerCase();
    }

    parseVariantAllowlist(text) {
      if (text == null || !String(text).trim()) return [];
      return String(text)
        .split(/[\n,]+/)
        .map((part) => this.normalizeVariantTitle(part))
        .filter(Boolean);
    }

    getRowVariantAllowlist(rowIndex) {
      const lists = this.activeSize?.rowVariantAllowlists;
      if (!Array.isArray(lists) || rowIndex < 0 || rowIndex >= lists.length) return [];
      return this.parseVariantAllowlist(lists[rowIndex]);
    }

    variantAllowedForRow(variant, allowlist) {
      if (!allowlist.length) return true;
      return allowlist.includes(this.normalizeVariantTitle(variant.title));
    }

    findVariantByTitle(size, title) {
      if (!size || !title) return null;
      const key = this.normalizeVariantTitle(title);
      return size.variants.find((v) => this.normalizeVariantTitle(v.title) === key) || null;
    }

    getRowIndex(rowId) {
      return this.rowOrder.indexOf(rowId);
    }

    syncLinkedSelectionAtIndex(rowIndex, slotIndex, variantTitle) {
      this.ensureSelectionCache();
      for (const size of this.sizes) {
        const cache = this.sizeSelectionCache.get(size.blockId);
        if (rowIndex < 0 || rowIndex >= size.rowCount) continue;
        const capacity = this.getSweetsPerRow(size, rowIndex);
        cache[rowIndex] = this.normalizeCacheRowEntry(cache[rowIndex], capacity);
        if (slotIndex >= 0 && slotIndex < capacity) {
          cache[rowIndex][slotIndex] = variantTitle || null;
        }
      }
    }

    persistRowOrderToAllSizeCaches() {
      this.ensureSelectionCache();
      const rowSlotTitles = this.rowOrder.map((id) => {
        const row = this.getRow(id);
        return (row?.slots || []).map((slot) => slot.variantTitle ?? null);
      });
      for (const size of this.sizes) {
        const cache = this.sizeSelectionCache.get(size.blockId);
        for (let i = 0; i < size.rowCount; i++) {
          const capacity = this.getSweetsPerRow(size, i);
          const source = rowSlotTitles[i] || [];
          const titles = Array(capacity).fill(null);
          for (let s = 0; s < capacity; s++) {
            titles[s] = source[s] ?? null;
          }
          cache[i] = titles;
        }
      }
    }

    buildRowsForSize(size) {
      this.ensureSelectionCache();
      const cache = this.sizeSelectionCache.get(size.blockId) || [];
      this.rows = [];
      for (let i = 0; i < size.rowCount; i++) {
        const capacity = this.getSweetsPerRow(size, i);
        const cachedTitles = this.normalizeCacheRowEntry(cache[i], capacity);
        const slots = [];
        for (let s = 0; s < capacity; s++) {
          const slot = { variantId: null, variantTitle: null };
          const title = cachedTitles[s];
          if (title) {
            const variant = this.findVariantByTitle(size, title);
            if (variant) {
              slot.variantId = variant.id;
              slot.variantTitle = variant.title;
            } else {
              slot.variantTitle = title;
            }
          }
          slots.push(slot);
        }
        this.rows.push({
          id: `row-${i}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          slots,
        });
      }
      this.rowOrder = this.rows.map((r) => r.id);
    }

    openBuilder(blockId, options = {}) {
      const size = this.sizeByBlockId.get(blockId);
      if (!size) return;

      this.activeSize = size;
      this.buildRowsForSize(size);
      this.root.dataset.buildBoxStep = 'build';
      this.chooseEl.hidden = true;
      this.builderEl.hidden = false;
      if (!options.skipUrlUpdate) {
        this.updateUrlForSize(size);
      }
      this.updateBuilderLead();
      this.render();
      this.builderEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    updateBuilderLead() {
      if (!this.builderLeadEl) return;
      const blockLead = this.activeSize?.builderLead;
      const sectionLead = this.strings.builderLead || '';
      const text =
        blockLead != null && String(blockLead).trim() ? String(blockLead).trim() : sectionLead;
      this.builderLeadEl.textContent = text;
    }

    showChooseStep() {
      this.root.dataset.buildBoxStep = 'choose-size';
      this.chooseEl.hidden = false;
      this.builderEl.hidden = true;
      this.clearSizeFromUrl();
      this.closeModal();
      this.closeSummaryModal();
    }

    upgradeSize(blockId) {
      const size = this.sizeByBlockId.get(blockId);
      if (!size || !this.activeSize) return;
      this.persistRowOrderToAllSizeCaches();
      this.activeSize = size;
      this.buildRowsForSize(size);
      this.updateUrlForSize(size);
      this.updateBuilderLead();
      this.render();
    }

    getRow(rowId) {
      return this.rows.find((r) => r.id === rowId);
    }

    setRowSlotVariant(rowId, slotIndex, variantId) {
      const row = this.getRow(rowId);
      if (!row?.slots?.[slotIndex]) return;
      const variant = variantId ? this.getVariant(variantId) : null;

      row.slots[slotIndex].variantId = variant ? variant.id : null;
      row.slots[slotIndex].variantTitle = variant ? variant.title : null;

      this.syncLinkedRowAtIndex(this.getRowIndex(rowId));
    }

    clearRowSelections(rowId) {
      const row = this.getRow(rowId);
      if (!row?.slots) return;
      row.slots.forEach((slot) => {
        slot.variantId = null;
        slot.variantTitle = null;
      });
      this.syncLinkedRowAtIndex(this.getRowIndex(rowId));
    }

    commitRowSlots(rowId, variantIds) {
      const row = this.getRow(rowId);
      if (!row?.slots) return;
      row.slots.forEach((slot, index) => {
        const variant = this.getVariant(variantIds[index]);
        slot.variantId = variant ? variant.id : null;
        slot.variantTitle = variant ? variant.title : null;
      });
      this.syncLinkedRowAtIndex(this.getRowIndex(rowId));
    }

    syncLinkedRowAtIndex(rowIndex) {
      if (rowIndex < 0) return;
      this.ensureSelectionCache();
      const rowId = this.rowOrder[rowIndex];
      const row = rowId ? this.getRow(rowId) : null;
      const sourceTitles = (row?.slots || []).map((slot) => slot.variantTitle ?? null);

      for (const size of this.sizes) {
        const cache = this.sizeSelectionCache.get(size.blockId);
        if (rowIndex >= size.rowCount) continue;
        const capacity = this.getSweetsPerRow(size, rowIndex);
        cache[rowIndex] = this.normalizeCacheRowEntry(
          sourceTitles.slice(0, capacity),
          capacity
        );
      }
    }

    getModalCapacity() {
      if (!this.modalRowId || !this.activeSize) return 1;
      const rowIndex = this.getRowIndex(this.modalRowId);
      return this.getSweetsPerRow(this.activeSize, rowIndex);
    }

    toggleModalVariant(variantId) {
      if (!Number.isFinite(variantId)) return;
      const capacity = this.getModalCapacity();
      const pending = [...this.modalPendingVariantIds];
      const existingIndex = pending.indexOf(variantId);

      if (capacity <= 1) {
        this.modalPendingVariantIds = existingIndex === 0 ? [] : [variantId];
      } else if (existingIndex !== -1) {
        pending.splice(existingIndex, 1);
        this.modalPendingVariantIds = pending;
      } else if (pending.length < capacity) {
        pending.push(variantId);
        this.modalPendingVariantIds = pending;
      }

      this.renderModalVariants(this.modalSearchEl?.value || '');
      this.updateModalSelectionText();
    }

    getVariant(variantId) {
      if (!this.activeSize || !variantId) return null;
      return this.activeSize.variants.find((v) => v.id === variantId) || null;
    }

    filledCount() {
      let count = 0;
      for (const rowId of this.rowOrder) {
        const row = this.getRow(rowId);
        if (!row?.slots) continue;
        for (const slot of row.slots) {
          if (slot?.variantId) count += 1;
        }
      }
      return count;
    }

    rowIsComplete(row) {
      return Boolean(row?.slots?.length && row.slots.every((slot) => slot?.variantId));
    }

    getRowLabelPrefix() {
      const blockPrefix = this.activeSize?.rowLabelPrefix;
      if (blockPrefix != null && String(blockPrefix).trim()) {
        return String(blockPrefix).trim();
      }
      return this.strings.rowLabel || 'Row';
    }

    getModalTitleForRow(displayIndex) {
      const titles = this.activeSize?.modalRowTitles;
      const custom = Array.isArray(titles) ? titles[displayIndex - 1] : null;
      const template =
        custom && String(custom).trim()
          ? String(custom)
          : this.strings.modalRowTitle || 'Row [row] — Choose a Sweet';
      return interpolate(template, { row: displayIndex });
    }

    openModal(rowId) {
      if (!this.activeSize) return;
      this.modalRowId = rowId;
      const row = this.getRow(rowId);
      this.modalPendingVariantIds = (row?.slots || [])
        .map((slot) => slot?.variantId)
        .filter((id) => id != null);
      const displayIndex = this.rowOrder.indexOf(rowId) + 1;
      this.modalTitleEl.textContent = this.getModalTitleForRow(displayIndex);
      if (this.modalSearchEl) this.modalSearchEl.value = '';
      if (this.modalGridEl) {
        this.modalGridEl.setAttribute(
          'aria-multiselectable',
          this.getModalCapacity() > 1 ? 'true' : 'false'
        );
      }
      this.renderModalVariants('');
      this.updateModalSelectionText();
      if (this._modalCloseTimer) {
        clearTimeout(this._modalCloseTimer);
        this._modalCloseTimer = null;
      }

      this.modalEl.hidden = false;
      this.modalEl.classList.remove('is-closing');
      document.body.classList.add('build-box-modal-open');

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.modalEl.classList.add('is-open');
        });
      });

      window.setTimeout(() => {
        this.modalSearchEl?.focus({ preventScroll: true });
      }, 120);
    }

    finishCloseModal() {
      if (this._modalCloseTimer) {
        clearTimeout(this._modalCloseTimer);
        this._modalCloseTimer = null;
      }
      if (!this.modalEl) return;
      this.modalEl.hidden = true;
      this.modalEl.classList.remove('is-open', 'is-closing');
      this.modalRowId = null;
      this.modalPendingVariantIds = [];
      document.body.classList.remove('build-box-modal-open');
    }

    closeModal() {
      if (!this.modalEl || this.modalEl.hidden) return;

      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reducedMotion || !this.modalEl.classList.contains('is-open')) {
        this.finishCloseModal();
        return;
      }

      if (this.modalEl.classList.contains('is-closing')) return;

      this.modalEl.classList.remove('is-open');
      this.modalEl.classList.add('is-closing');

      const panel = this.modalEl.querySelector('.build-box__modal-panel');
      const onEnd = (event) => {
        if (event.target !== panel) return;
        panel.removeEventListener('animationend', onEnd);
        this.finishCloseModal();
      };

      if (panel) {
        panel.addEventListener('animationend', onEnd);
      }

      this._modalCloseTimer = window.setTimeout(() => {
        if (panel) panel.removeEventListener('animationend', onEnd);
        this.finishCloseModal();
      }, 400);
    }

    commitModalSelection() {
      if (this.modalRowId != null && this.modalDoneBtn && !this.modalDoneBtn.disabled) {
        const capacity = this.getModalCapacity();
        const ids = this.modalPendingVariantIds.slice(0, capacity);
        const slots = Array(capacity).fill(null);
        ids.forEach((id, index) => {
          slots[index] = id;
        });
        this.commitRowSlots(this.modalRowId, slots);
      }
      this.closeModal();
      this.render();
    }

    updateModalSelectionText() {
      const capacity = this.getModalCapacity();
      const pending = this.modalPendingVariantIds;
      const pendingCount = pending.length;
      const remaining = Math.max(0, capacity - pendingCount);
      const titles = pending
        .map((id) => this.getVariant(id)?.title)
        .filter(Boolean);

      if (this.modalSelectHintEl) {
        if (capacity > 1 && pendingCount > 0 && remaining > 0) {
          this.modalSelectHintEl.hidden = false;
          this.modalSelectHintEl.textContent = interpolate(this.strings.modalSelectMore, {
            count: remaining,
            remaining,
            selected: pendingCount,
            capacity,
          });
        } else {
          this.modalSelectHintEl.hidden = true;
          this.modalSelectHintEl.textContent = '';
        }
      }

      if (this.modalSelectionEl) {
        if (!titles.length) {
          this.modalSelectionEl.textContent = this.strings.modalNoSelection || '';
        } else if (capacity > 1) {
          this.modalSelectionEl.textContent = `${titles.length} / ${capacity} — ${titles.join(', ')}`;
        } else {
          this.modalSelectionEl.textContent = titles[0];
        }
      }

      if (this.modalDoneBtn) {
        if (capacity <= 1) {
          this.modalDoneBtn.textContent = this.modalDoneDefaultLabel;
          this.modalDoneBtn.disabled = pendingCount !== 1;
        } else {
          const pendingLabel = interpolate(this.strings.modalDonePending, {
            selected: pendingCount,
            capacity,
            remaining,
            count: remaining,
          });
          this.modalDoneBtn.textContent =
            pendingLabel && String(pendingLabel).trim()
              ? pendingLabel
              : `${this.modalDoneDefaultLabel} (${pendingCount}/${capacity})`;
          this.modalDoneBtn.disabled = pendingCount !== capacity;
        }
      }
    }

    renderModalVariants(query) {
      if (!this.modalGridEl || !this.activeSize) return;
      const q = query.trim().toLowerCase();
      const rowIndex = this.modalRowId != null ? this.getRowIndex(this.modalRowId) : -1;
      const allowlist = this.getRowVariantAllowlist(rowIndex);

      let variants = this.activeSize.variants.filter((v) => {
        if (!this.variantAllowedForRow(v, allowlist)) return false;
        if (!q) return true;
        return v.title.toLowerCase().includes(q);
      });

      if (allowlist.length && this.modalPendingVariantIds.length) {
        const visibleIds = new Set(variants.map((v) => v.id));
        this.modalPendingVariantIds.forEach((id) => {
          if (visibleIds.has(id)) return;
          const variant = this.getVariant(id);
          if (variant) {
            variants.push(variant);
            visibleIds.add(id);
          }
        });
      }

      this.modalGridEl.innerHTML = variants
        .map((variant) => {
          const selected = this.modalPendingVariantIds.includes(variant.id);
          const tag =
            variant.variantTag != null && String(variant.variantTag).trim() !== ''
              ? `<span class="build-box__variant-tag">${escapeHtml(variant.variantTag)}</span>`
              : '';
          const img = variant.image
            ? `<img src="${escapeHtml(variant.image)}" alt="" loading="lazy" width="480" height="480">`
            : `<div class="build-box__variant-placeholder"></div>`;
          return `
            <button
              type="button"
              class="build-box__variant-option${selected ? ' is-selected' : ''}"
              data-build-box-variant-option
              data-variant-id="${variant.id}"
              role="option"
              aria-selected="${selected}"
              ${variant.available ? '' : 'disabled'}
            >
              <div class="build-box__variant-media">
                ${tag}
                ${img}
              </div>
              <span class="build-box__variant-title">${escapeHtml(variant.title)}</span>
              ${
                this.shouldHideVariantPrices()
                  ? ''
                  : `<span class="build-box__variant-price">${formatMoney(variant.price, this.config.shopMoneyFormat)}</span>`
              }
            </button>
          `;
        })
        .join('');
    }

    render() {
      this.syncHideVariantPricesFlag();
      this.renderRows();
      this.renderProgress();
      this.renderSummary();
      this.renderUpgrade();
    }

    renderRowSelectionBlock(variant, slotIndex, multiSlot) {
      const tag =
        variant.variantTag != null && String(variant.variantTag).trim() !== ''
          ? `<span class="build-box__row-tag">${escapeHtml(variant.variantTag)}</span>`
          : '';
      const img = variant.image
        ? `<img src="${escapeHtml(variant.image)}" alt="" loading="lazy" width="120" height="120">`
        : '';
      const removeAttrs = multiSlot ? ` data-slot-index="${slotIndex}"` : '';
      return `
        <div class="build-box__row-selection">
          <div class="build-box__row-selection-media">
            ${img}
            <span class="build-box__row-check" aria-hidden="true">✓</span>
          </div>
          <div class="build-box__row-selection-body">
            <p class="build-box__row-selection-title">${escapeHtml(variant.title)}</p>
            ${tag}
            ${
              this.shouldHideVariantPrices()
                ? ''
                : `<p class="build-box__row-selection-price">${formatMoney(variant.price, this.config.shopMoneyFormat)}</p>`
            }
          </div>
          <button type="button" class="build-box__row-remove" data-build-box-remove-row${removeAttrs} aria-label="${escapeHtml(this.strings.removeSweet || 'Remove')}">×</button>
        </div>
      `;
    }

    renderRows() {
      if (!this.rowsEl || !this.activeSize) return;
      this.rowsEl.innerHTML = this.rowOrder
        .map((rowId, index) => {
          const row = this.getRow(rowId);
          const rowNum = index + 1;
          const label = `${this.getRowLabelPrefix()} ${rowNum}`.toUpperCase();
          const capacity = this.getSweetsPerRow(this.activeSize, index);
          const multiSlot = capacity > 1;
          const rowComplete = this.rowIsComplete(row);
          const selectionsHtml = (row?.slots || [])
            .map((slot, slotIndex) => {
              const variant = this.getVariant(slot?.variantId);
              if (!variant) return '';
              return this.renderRowSelectionBlock(variant, slotIndex, multiSlot);
            })
            .filter(Boolean)
            .join('');
          const selectionsWrap = selectionsHtml
            ? `<div class="build-box__row-selections">${selectionsHtml}</div>`
            : '';

          if (rowComplete) {
            return `
              <article class="build-box__row build-box__row--filled" data-build-box-row data-row-id="${rowId}">
                <header class="build-box__row-header">
                  <div class="build-box__row-header-start">
                    <button type="button" class="build-box__drag-handle" data-build-box-drag-handle draggable="true" aria-label="Reorder row ${rowNum}">⋮⋮</button>
                    <span class="build-box__row-label">${escapeHtml(label)}</span>
                    <span class="build-box__row-selected-badge">${escapeHtml(this.strings.selectedBadge || 'Selected')}</span>
                  </div>
                  <div class="build-box__row-header-actions">
                    <button type="button" class="build-box__row-action" data-build-box-open-row>${escapeHtml(this.strings.changeSweet || 'Change')}</button>
                  </div>
                </header>
                ${selectionsWrap}
              </article>
            `;
          }

          if (selectionsHtml) {
            return `
              <article class="build-box__row build-box__row--empty" data-build-box-row data-row-id="${rowId}">
                <header class="build-box__row-header">
                  <div class="build-box__row-header-start">
                    <button type="button" class="build-box__drag-handle" data-build-box-drag-handle draggable="true" aria-label="Reorder row ${rowNum}">⋮⋮</button>
                    <span class="build-box__row-label">${escapeHtml(label)}</span>
                  </div>
                  <button type="button" class="build-box__choose-sweet" data-build-box-open-row>+ ${escapeHtml(this.strings.chooseSweet || 'Choose Sweet')}</button>
                </header>
                ${selectionsWrap}
              </article>
            `;
          }

          return `
            <article class="build-box__row build-box__row--empty" data-build-box-row data-row-id="${rowId}">
              <header class="build-box__row-header">
                <div class="build-box__row-header-start">
                  <button type="button" class="build-box__drag-handle" data-build-box-drag-handle draggable="true" aria-label="Reorder row ${rowNum}">⋮⋮</button>
                  <span class="build-box__row-label">${escapeHtml(label)}</span>
                </div>
                <button type="button" class="build-box__choose-sweet" data-build-box-open-row>+ ${escapeHtml(this.strings.chooseSweet || 'Choose Sweet')}</button>
              </header>
              <button type="button" class="build-box__row-empty" data-build-box-open-row>
                <span class="build-box__row-empty-icon" aria-hidden="true">+</span>
                <span class="build-box__row-empty-text">${escapeHtml(this.strings.emptyRowHint || '')}</span>
              </button>
            </article>
          `;
        })
        .join('');
    }

    renderProgress() {
      if (!this.activeSize) return;
      const total = this.totalSlotCount();
      const count = this.filledCount();
      const percent = total ? Math.round((count / total) * 100) : 0;

      if (this.progressCountEl) {
        this.progressCountEl.textContent = interpolate(this.strings.progressChosen, { count, total });
      }
      if (this.stickyCountEl) {
        this.stickyCountEl.textContent = `${count} of ${total} sweets`;
      }
      if (this.progressPercentEl) {
        this.progressPercentEl.textContent = `${percent}%`;
      }
      if (this.progressSizeEl) {
        const suffix = interpolate(this.strings.progressSizeSuffix, {
          title: this.activeSize.title,
          total: this.activeSize.totalSweets,
        });
        this.progressSizeEl.textContent = suffix;
      }
      if (this.progressFillEl) {
        this.progressFillEl.style.width = `${percent}%`;
      }
      if (this.progressTrackEl) {
        this.progressTrackEl.setAttribute('aria-valuenow', String(percent));
        this.progressTrackEl.setAttribute('aria-valuemax', '100');
      }
      this.summaryBlocks.forEach((block) => {
        if (block.progressFillEl) {
          block.progressFillEl.style.width = `${percent}%`;
        }
      });

      const remaining = total - count;
      if (this.builderStatusEl) {
        if (remaining <= 0) {
          this.builderStatusEl.hidden = true;
          this.builderStatusEl.textContent = '';
        } else if (remaining === 1) {
          this.builderStatusEl.hidden = false;
          this.builderStatusEl.textContent = this.strings.rowsRemainingOne || '';
        } else {
          this.builderStatusEl.hidden = false;
          this.builderStatusEl.textContent = interpolate(this.strings.rowsRemainingOther, { count: remaining });
        }
      }

      if (this.builderLeadEl) {
        const keepVisible = this.activeSize?.builderLeadOverlay === true;
        this.builderLeadEl.hidden = !keepVisible && count > 0;
      }
    }

    renderSummary() {
      if (!this.activeSize) return;
      const count = this.filledCount();
      const total = this.totalSlotCount();
      const complete = count >= total && total > 0;

      let sweetsTotal = 0;
      const items = [];
      this.rowOrder.forEach((rowId, rowIndex) => {
        const row = this.getRow(rowId);
        if (!row?.slots) return;
        row.slots.forEach((slot, slotIndex) => {
          const variant = this.getVariant(slot?.variantId);
          if (!variant) return;
          sweetsTotal += variant.price;
          items.push({ rowIndex: rowIndex + 1, slotIndex: slotIndex + 1, variant });
        });
      });

      const listHtml = items
        .map(
          ({ variant }) => `
          <li class="build-box__summary-item">
            <span class="build-box__summary-item-title">${escapeHtml(variant.title)}</span>
            ${
              this.shouldHideVariantPrices()
                ? ''
                : `<span class="build-box__summary-item-price">${formatMoney(variant.price, this.config.shopMoneyFormat)}</span>`
            }
          </li>
        `
        )
        .join('');

      const remaining = total - count;
      const noteText =
        remaining > 0 && count > 0
          ? remaining === 1
            ? this.strings.summaryNoteOne || ''
            : interpolate(this.strings.summaryNoteOther, { count: remaining })
          : '';
      const ctaLabel = complete
        ? this.strings.addToCartComplete || 'Add to Cart'
        : this.strings.addToCartIncomplete || 'Choose Sweets to Continue';

      this.summaryBlocks.forEach((block) => {
        if (block.titleEl) block.titleEl.textContent = this.activeSize.title;
        if (block.emptyEl) block.emptyEl.hidden = items.length > 0;
        if (block.listEl) {
          block.listEl.hidden = items.length === 0;
          block.listEl.innerHTML = listHtml;
        }
        if (block.sweetsLabelEl) {
          block.sweetsLabelEl.textContent = interpolate(this.strings.summarySweets, { count: items.length });
        }
        if (block.sweetsPriceEl) {
          block.sweetsPriceEl.textContent = formatMoney(sweetsTotal, this.config.shopMoneyFormat);
        }
        if (block.totalEl) {
          block.totalEl.textContent = formatMoney(sweetsTotal, this.config.shopMoneyFormat);
        }
        if (block.noteEl) {
          if (noteText) {
            block.noteEl.hidden = false;
            block.noteEl.textContent = noteText;
          } else {
            block.noteEl.hidden = true;
          }
        }
      });

      this.addToCartBtns.forEach((btn) => {
        btn.disabled = !complete;
        btn.textContent = ctaLabel;
      });

      if (this.stickyTotalEl) {
        this.stickyTotalEl.textContent = formatMoney(sweetsTotal, this.config.shopMoneyFormat);
      }
    }

    openSummaryModal() {
      if (!this.summaryModalEl) return;

      if (this._summaryCloseTimer) {
        clearTimeout(this._summaryCloseTimer);
        this._summaryCloseTimer = null;
      }

      this.summaryModalEl.hidden = false;
      this.summaryModalEl.classList.remove('is-closing');
      document.body.classList.add('build-box-summary-modal-open');

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.summaryModalEl.classList.add('is-open');
        });
      });
    }

    finishCloseSummaryModal() {
      if (this._summaryCloseTimer) {
        clearTimeout(this._summaryCloseTimer);
        this._summaryCloseTimer = null;
      }
      if (!this.summaryModalEl) return;
      this.summaryModalEl.hidden = true;
      this.summaryModalEl.classList.remove('is-open', 'is-closing');
      document.body.classList.remove('build-box-summary-modal-open');
    }

    closeSummaryModal() {
      if (!this.summaryModalEl || this.summaryModalEl.hidden) return;

      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reducedMotion || !this.summaryModalEl.classList.contains('is-open')) {
        this.finishCloseSummaryModal();
        return;
      }

      if (this.summaryModalEl.classList.contains('is-closing')) return;

      this.summaryModalEl.classList.remove('is-open');
      this.summaryModalEl.classList.add('is-closing');

      const panel = this.summaryModalEl.querySelector('.build-box__summary-modal-panel');
      const onEnd = (event) => {
        if (event.target !== panel) return;
        panel.removeEventListener('animationend', onEnd);
        this.finishCloseSummaryModal();
      };

      if (panel) {
        panel.addEventListener('animationend', onEnd);
      }

      this._summaryCloseTimer = window.setTimeout(() => {
        if (panel) panel.removeEventListener('animationend', onEnd);
        this.finishCloseSummaryModal();
      }, 400);
    }

    buildCartItems() {
      const groupId = `${this.activeSize.blockId}-${Date.now()}`;
      const rowLabelPrefix = this.getRowLabelPrefix();
      const items = [];

      this.rowOrder.forEach((rowId, rowIndex) => {
        const row = this.getRow(rowId);
        if (!row?.slots) return;
        const rowCapacity = this.getSweetsPerRow(this.activeSize, rowIndex);

        row.slots.forEach((slot, slotIndex) => {
          const variant = this.getVariant(slot?.variantId);
          if (!variant) return;

          const rowPropertyKey =
            rowCapacity > 1
              ? `${rowLabelPrefix} ${rowIndex + 1} - ${slotIndex + 1}`
              : `${rowLabelPrefix} ${rowIndex + 1}`;

          const sourceProps = window.KROWN?.functions?.getCartLineItemSourceProperties?.(this.root) || {
            _source: 'build box page',
          };

          items.push({
            id: variant.id,
            quantity: 1,
            properties: {
              _build_box: this.activeSize.title,
              _build_box_group: groupId,
              _build_box_product_id: String(this.activeSize.productId),
              ...(variant.image ? { _variant_image: variant.image } : {}),
              ...sourceProps,
              [rowPropertyKey]: variant.title,
            },
          });
        });
      });

      return items;
    }

    async addToCartAndCheckout() {
      const primaryBtn = this.addToCartBtns[0];
      if (!this.activeSize || !primaryBtn || primaryBtn.disabled) return;

      const items = this.buildCartItems();
      if (!items.length) return;

      const cartAddUrl = this.config.cartAddUrl || '/cart/add.js';

      const previousLabel = primaryBtn.textContent;
      const loadingLabel = this.strings.addToCartLoading || 'Adding…';
      this.addToCartBtns.forEach((btn) => {
        btn.disabled = true;
        btn.textContent = loadingLabel;
      });
      this.closeSummaryModal();

      try {
        const response = await fetch(cartAddUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({ items }),
        });

        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          const message = data.description || data.message || 'Could not add items to cart.';
          throw new Error(message);
        }

        if (typeof window.refreshCart === 'function') {
          window.refreshCart(true);
        } else {
          const drawer =
            document.querySelector('[data-js-site-cart-sidebar]') ||
            document.getElementById('site-cart-sidebar');
          if (drawer && typeof drawer.show === 'function') {
            drawer.show();
          }
        }

        this.addToCartBtns.forEach((btn) => {
          btn.disabled = false;
        });
        this.renderSummary();

        if (document.getElementById('screen-reader-info') && window.KROWN?.settings?.locales?.cart_announcement) {
          document.getElementById('screen-reader-info').innerText = window.KROWN.settings.locales.cart_announcement;
          setTimeout(() => {
            document.getElementById('screen-reader-info').innerText = '';
          }, 1000);
        }
      } catch (error) {
        this.addToCartBtns.forEach((btn) => {
          btn.disabled = false;
          btn.textContent = previousLabel;
        });
        this.renderSummary();
        window.alert(error.message || 'Could not add items to cart.');
      }
    }

    renderUpgrade() {
      if (!this.upgradeEl || !this.upgradeActionsEl || !this.activeSize) return;
      const upgrades = this.sizes.filter((s) => s.rowCount > this.activeSize.rowCount);
      const minFilledForUpgrade = parseInt(this.config.upgradeMinFilledRows, 10) || 2;
      const filled = this.filledCount();
      if (!upgrades.length || filled < minFilledForUpgrade) {
        this.upgradeEl.hidden = true;
        this.upgradeActionsEl.innerHTML = '';
        return;
      }
      this.upgradeEl.hidden = false;
      this.upgradeActionsEl.innerHTML = upgrades
        .map((size) => {
          const label = interpolate(this.strings.upgradeButton, {
            rows: size.rowCount,
            title: size.title,
            price: formatMoney(size.boxPrice, this.config.shopMoneyFormat),
          });
          return `<button type="button" class="build-box__upgrade-btn" data-build-box-upgrade-size data-block-id="${escapeHtml(size.blockId)}">${escapeHtml(label)}</button>`;
        })
        .join('');
    }
  }

  function init() {
    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => new BuildBox(root));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (e) => {
    const root = e.target.querySelector(ROOT_SELECTOR);
    if (root) new BuildBox(root);
  });
})();
